/**
 * use-product-metadata.ts — product_metadata 业务效果录入 API 封装（ADM-PAY · 阶段1 接真）
 *
 * 对应后端 /api/v1/product-metadata（packages/server routes/v1/product-metadata）：运营在 Adapty 加 product 后，
 * 按 vendor_product_id 在 admin 录入「业务效果」（coin/bonus/有效期/标题）。这些是 Adapty 不覆盖、
 * 必须运营人填的字段——与 paymentPagesApi（paywall 渠道配置）是两套不同的表/数据。
 *
 * 每方法 = 一次 apiRequest（自动带 JWT + X-Request-Id + 解 envelope 返 data，失败抛 AdminApiError）。
 * 请求/响应类型一律从 @novel-admin/shared 取（禁 web 侧重定义）。RBAC（后端权威）：
 * list/GET 任意已登录；create/update root/admin/operator；activate/deactivate（启停）与 remove/DELETE
 * 收窄 root/admin —— 停用 is_active=false 与软删均使 C 端 webhook 按 vendor_product_id 反查不命中（C 端过滤
 * `is_active=true AND deleted_at IS NULL`）→ 用户已扣款漏加 coin/bonus，故启停与删除同级高危。
 */

import type {
  CreateProductMetadataRequest,
  NativeChannelPricesResponse,
  ProductMetadataResponse,
  PutNativeChannelPricesRequest,
  ReplaceReadingPassTiersRequest,
  UpdateProductMetadataRequest,
} from '@novel-admin/shared';
import { apiRequest } from '~/api/client';

export const productMetadataApi = {
  /** 列某 App 全部业务效果配置（per-App 有界集，后端不分页全量返回） */
  list: (appId: string) =>
    apiRequest<ProductMetadataResponse[]>({
      method: 'GET',
      path: '/api/v1/product-metadata',
      query: { app_id: appId },
    }),
  /** 录入新行（resurrection upsert：软删行复活 / 存活则 RESOURCE_CONFLICT，由后端裁决） */
  create: (body: CreateProductMetadataRequest) =>
    apiRequest<ProductMetadataResponse>({
      method: 'POST',
      path: '/api/v1/product-metadata',
      body,
    }),
  /** 改业务效果（Update .strict() 拒身份/不可变字段 + is_active：vendor_product_id/product_type/app_id/id/is_active 误传即 422） */
  update: (id: string, body: UpdateProductMetadataRequest) =>
    apiRequest<ProductMetadataResponse>({
      method: 'PATCH',
      path: `/api/v1/product-metadata/${id}`,
      body,
    }),
  /** 原子换代（A8 §5.1）：按 access_level_id 上架 / 下架阅读卡配对行 */
  replaceReadingPassTiers: (body: ReplaceReadingPassTiersRequest) =>
    apiRequest<ProductMetadataResponse[]>({
      method: 'POST',
      path: '/api/v1/product-metadata/reading-pass-tiers/replace',
      body,
    }),
  /** 上架（启用业务效果，高危收窄 root/admin） */
  activate: (id: string) =>
    apiRequest<ProductMetadataResponse>({
      method: 'POST',
      path: `/api/v1/product-metadata/${id}/activate`,
    }),
  /** 下架（停用业务效果，高危收窄 root/admin——停用使 C 端 webhook 反查不命中→漏加 coin/bonus，与 DELETE 同级） */
  deactivate: (id: string) =>
    apiRequest<ProductMetadataResponse>({
      method: 'POST',
      path: `/api/v1/product-metadata/${id}/deactivate`,
    }),
  /** 框架通道行的原生价格（A8 §10.3，viewer+）：`prices=[]` = 未定价 */
  getChannelPrices: (id: string) =>
    apiRequest<NativeChannelPricesResponse>({
      method: 'GET',
      path: `/api/v1/product-metadata/${id}/channel-prices`,
    }),
  /**
   * 整组替换框架通道行的原生价格（A8 §10.3，root/admin + mfa）；`prices=[]` = 清除（该通道不可售）。
   * 必带 UUID v7 `Idempotency-Key`：同一意图重试复用、内容变了换新（调用方维护）。无版本栅栏，后提交者生效，
   * 调用方以响应覆盖本地。
   */
  putChannelPrices: (id: string, body: PutNativeChannelPricesRequest, idempotencyKey: string) =>
    apiRequest<NativeChannelPricesResponse>({
      method: 'PUT',
      path: `/api/v1/product-metadata/${id}/channel-prices`,
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  /** 软删（高危，仅 root/admin）；返 { id, deleted_at }（软删壳） */
  remove: (id: string) =>
    apiRequest<{ id: string; deleted_at: string }>({
      method: 'DELETE',
      path: `/api/v1/product-metadata/${id}`,
    }),
};
